import type { Route } from 'next';
import React from 'react';
import { useForm } from 'react-hook-form';
import {
  api_portalOfficeListing_filter,
  service_ResponseForNonMemberPortalOfficeListing,
} from '@/init/swagger';
import {
  SearchStationDetailType,
  SearchStationDetailResolver,
} from '@/schemas/SearchStationDetailSchema';
import { useSnackbar } from '@/hooks/useSnackbar';
import {
  STATION_ADVANCED_SEARCH_TYPES,
  STATION_SORT_VALUE_TYPES,
  STATION_LIMIT,
  STATION_ADVANCED_SEARCH_INITIAL_ZIPCODE,
  LISTING_FILTER_CATEGORY_TYPE,
} from '@/types/types';
import { pathInfo } from '@/types/pathInfo';
import { useBreakPoints } from '@/hooks/useBreakPoints';
import { PaginationProps } from '@mui/material';
import { ParsedPublicStationsSearchPageSearchParams } from '@/schemas/SearchParamsSchemas/public/stations/search/Schema';
import { ServerErrorInfo } from '@/helpers/serverErrorSnackbarHelper';
import { useNextRouter } from '@/hooks/useNextRouter';
import { PublicListingListRef } from '@/features/station/PublicListingList';
import type { PublicSearchFilterType } from '@/features/station/PublicSearchFilterBox/logic';

type Args = {
  searchFilters: api_portalOfficeListing_filter[];
  parsedSearchParams: ParsedPublicStationsSearchPageSearchParams;
  portalOfficeListings?: service_ResponseForNonMemberPortalOfficeListing[];
  allPortalOfficeListingTotalCount?: number;
  autoTargetAddressTextByPostCode?: string;
  serverError?: ServerErrorInfo | null;
};

export const useLogic = ({
  parsedSearchParams,
  portalOfficeListings: initialPortalOfficeListings,
  allPortalOfficeListingTotalCount: initialAllPortalOfficeListingTotalCount,
  autoTargetAddressTextByPostCode: initialAutoTargetAddressTextByPostCode,
  serverError: initialServerError,
}: Args) => {
  const router = useNextRouter();
  const { isMobile } = useBreakPoints();

  const searchParamsPostCode = parsedSearchParams.postCode;
  const defaultFormValuePostCode = searchParamsPostCode || '';

  const searchParamsPrefName = parsedSearchParams.prefName;
  const defaultFormValuePrefName = searchParamsPrefName || '';

  const searchParamsSearchType = parsedSearchParams.searchType;
  const defaultFormValueSearchType = searchParamsSearchType;

  // 郵便番号も住所も全て空の場合の郵便番号の初期値を設定
  const initialValuePostCode =
    defaultFormValueSearchType === STATION_ADVANCED_SEARCH_TYPES.ZIPCODE &&
    !defaultFormValuePostCode
      ? STATION_ADVANCED_SEARCH_INITIAL_ZIPCODE
      : defaultFormValuePostCode;

  const defaultFormValueAddress = parsedSearchParams.address;
  const defaultFormValuePartnerName = parsedSearchParams.partnerName;

  const searchParamsSearchFilters = parsedSearchParams['searchFilters[]'];

  const searchParamsMovingMinutes = parsedSearchParams.movingMinutes;
  const defaultFormValueMovingMinutes = searchParamsMovingMinutes;

  const defaultFormValueSearchFilters = searchParamsSearchFilters;

  const searchParamsIsNoAddition = parsedSearchParams.isNoAddition;

  const searchParamsSort = parsedSearchParams.sort;
  const defaultFormValueSort = searchParamsSort;

  const searchParamsPage = parsedSearchParams.page;

  const defaultFormValuePage = searchParamsPage;

  const defaultOffset = (defaultFormValuePage - 1) * STATION_LIMIT;

  const searchParamsIsShowMap = parsedSearchParams.isShowMap;
  const defaultIsShowMap = searchParamsIsShowMap;
  const [isShowMap, setIsShowMap] = React.useState(defaultIsShowMap);

  // サーバーから渡されたデータをそのまま使用（クライアント側での状態管理は不要）
  const listings = initialPortalOfficeListings;
  const allListingTotalCount = initialAllPortalOfficeListingTotalCount;

  // ページネーション情報をサーバーから渡されたデータで計算
  const pages = allListingTotalCount
    ? Array.from(
        { length: Math.ceil(allListingTotalCount / STATION_LIMIT) },
        (_, i) => i + 1,
      )
    : [];

  const [searchFilterEl, setSearchFilterEl] =
    React.useState<HTMLButtonElement | null>(null);
  const [searchFilterType, setSearchFilterType] =
    React.useState<PublicSearchFilterType>(null);
  const [hoveredMarkerListingId, setHoveredMarkerListingId] = React.useState<
    number | null
  >(listings?.[0]?.id || null);
  const listingListRef = React.useRef<PublicListingListRef>(null);
  const prevPageRef = React.useRef(defaultFormValuePage);
  // サーバーから渡された住所テキストをそのまま使用
  const targetAddress = initialAutoTargetAddressTextByPostCode || '';
  const { openSnackbar } = useSnackbar();

  // 初期サーバーエラーがある場合は表示
  React.useEffect(() => {
    if (initialServerError) {
      openSnackbar(initialServerError.message, 'error');
    }
  }, [initialServerError, openSnackbar]);

  const formMethod = useForm<SearchStationDetailType>({
    resolver: SearchStationDetailResolver,
    defaultValues: {
      searchType: defaultFormValueSearchType,
      location: {
        pref_name: defaultFormValuePrefName,
        zipcode: initialValuePostCode,
        moving_minutes: defaultFormValueMovingMinutes,
        address: defaultFormValueAddress,
      },
      partnerName: defaultFormValuePartnerName,
      filters: defaultFormValueSearchFilters,
      sort: defaultFormValueSort,
      page: defaultFormValuePage,
      is_no_addition: searchParamsIsNoAddition,
    },
  });

  const paginationInfo = {
    current: {
      from: defaultOffset + 1,
      to: defaultOffset + STATION_LIMIT,
      page: formMethod.watch('page') ?? 1,
    },
    pages,
    lastPage: pages.length,
    allListingTotalCount,
    displayListingCount: listings?.length,
    isShowPagination:
      allListingTotalCount !== undefined &&
      allListingTotalCount > STATION_LIMIT,
  };

  formMethod.register('sort', {
    onChange: async (event: React.ChangeEvent<HTMLInputElement>) => {
      formMethod.setValue(
        'sort',
        event.target.value as STATION_SORT_VALUE_TYPES,
      );
      // ソート変更時もページ番号を1にリセット
      formMethod.setValue('page', 1);

      // サーバー側検索のためルーター遷移のみ実行
      searchRoutePush({
        searchType: formMethod.getValues('searchType'),
        postCode: formMethod.getValues('location.zipcode'),
        prefName: formMethod.getValues('location.pref_name'),
        address: formMethod.getValues('location.address'),
        partnerName: formMethod.getValues('partnerName'),
        movingMinutes: formMethod.getValues('location.moving_minutes'),
        sort: formMethod.getValues('sort'),
        filters: formMethod.getValues('filters'),
        isNoAddition: formMethod.getValues('is_no_addition'),
      });
    },
  });

  const gotoPageForPagination = async (page: number) => {
    formMethod.setValue('page', page);

    const newQuery = new URLSearchParams(window.location.search);
    newQuery.set(
      pathInfo['/public/stations/search'].searchParams.page,
      page.toString(),
    );

    // サーバー側検索のためルーター遷移のみ実行
    router.push(`/public/stations/search?${newQuery.toString()}`);
  };

  formMethod.register('page', {
    onChange: async (event: React.ChangeEvent<HTMLInputElement>) => {
      const page = +event.target.value;

      await gotoPageForPagination(page);
    },
  });

  const searchRoutePush = async ({
    searchType,
    postCode,
    prefName,
    address,
    partnerName,
    movingMinutes,
    sort,
    filters,
    isNoAddition,
  }: {
    searchType: SearchStationDetailType['searchType'];
    postCode: SearchStationDetailType['location']['zipcode'];
    prefName: SearchStationDetailType['location']['pref_name'];
    address: SearchStationDetailType['location']['address'];
    partnerName?: SearchStationDetailType['partnerName'];
    movingMinutes: SearchStationDetailType['location']['moving_minutes'];
    sort: SearchStationDetailType['sort'];
    filters: SearchStationDetailType['filters'];
    isNoAddition: SearchStationDetailType['is_no_addition'];
  }) => {
    // 新しいクエリパラメータを作成（現在のパラメータをクリア）
    const newQuery = new URLSearchParams();

    // 検索タイプを設定
    if (searchType) {
      newQuery.set(
        pathInfo['/public/stations/search'].searchParams.searchType,
        searchType,
      );
    }

    // 検索タイプに応じて位置情報を設定
    if (searchType === STATION_ADVANCED_SEARCH_TYPES.ZIPCODE) {
      if (postCode) {
        newQuery.set(
          pathInfo['/public/stations/search'].searchParams.postCode,
          postCode,
        );
      }
      // 住所検索・地域連携先検索のパラメータをクリア
      newQuery.delete(
        pathInfo['/public/stations/search'].searchParams.prefName,
      );
      newQuery.delete(pathInfo['/public/stations/search'].searchParams.address);
      newQuery.delete(
        pathInfo['/public/stations/search'].searchParams.partnerName,
      );
    } else if (searchType === STATION_ADVANCED_SEARCH_TYPES.ADDRESS) {
      if (prefName) {
        newQuery.set(
          pathInfo['/public/stations/search'].searchParams.prefName,
          prefName,
        );
      }
      if (address) {
        newQuery.set(
          pathInfo['/public/stations/search'].searchParams.address,
          address,
        );
      }
      // 郵便番号検索・地域連携先検索のパラメータをクリア
      newQuery.delete(
        pathInfo['/public/stations/search'].searchParams.postCode,
      );
      newQuery.delete(
        pathInfo['/public/stations/search'].searchParams.partnerName,
      );
    } else if (searchType === STATION_ADVANCED_SEARCH_TYPES.PARTNER) {
      if (prefName) {
        newQuery.set(
          pathInfo['/public/stations/search'].searchParams.prefName,
          prefName,
        );
      }
      if (partnerName) {
        newQuery.set(
          pathInfo['/public/stations/search'].searchParams.partnerName,
          partnerName,
        );
      }
      // 郵便番号検索・住所検索のパラメータをクリア
      newQuery.delete(
        pathInfo['/public/stations/search'].searchParams.postCode,
      );
      newQuery.delete(pathInfo['/public/stations/search'].searchParams.address);
    }

    // 移動時間を設定
    if (movingMinutes) {
      newQuery.set(
        pathInfo['/public/stations/search'].searchParams.movingMinutes,
        movingMinutes,
      );
    }

    // 検索フィルターを設定
    const searchFilters =
      filters?.map(
        (filter) => `${filter.category_id}_${filter.search_filter_id}`,
      ) ?? [];
    searchFilters.forEach((filter) => {
      newQuery.append(
        pathInfo['/public/stations/search'].searchParams.searchFilters,
        filter,
      );
    });

    // ソートを設定
    if (sort) {
      newQuery.set(pathInfo['/public/stations/search'].searchParams.sort, sort);
    }

    // 追加ステータス除外フラグを設定
    if (isNoAddition) {
      newQuery.set(
        pathInfo['/public/stations/search'].searchParams.isNoAddition,
        isNoAddition.toString(),
      );
    }

    // ページ番号を設定（検索時は常に1ページ目）
    const currentPage = formMethod.getValues('page');
    if (currentPage) {
      newQuery.set(
        pathInfo['/public/stations/search'].searchParams.page,
        currentPage.toString(),
      );
    }

    // ルーター遷移を実行
    const newUrl = `/public/stations/search?${newQuery.toString()}`;
    router.push(newUrl as Route);
  };

  const getSelectedSearchFiltersByGroup = (
    searchFilterType: string,
  ): api_portalOfficeListing_filter[] => {
    return defaultFormValueSearchFilters.filter((filter) => {
      if (searchFilterType === 'serviceAndAdditionStatus') {
        return (
          filter.category_id == LISTING_FILTER_CATEGORY_TYPE.SERVICE ||
          filter.category_id == LISTING_FILTER_CATEGORY_TYPE.ADDITION_STATUS ||
          filter.category_id == LISTING_FILTER_CATEGORY_TYPE.OTHERS
        );
      }
    });
  };

  const getSelectedSearchFilterTexts = (
    filters: api_portalOfficeListing_filter[],
  ): string => {
    const maxDisplayCount = 3;

    const filterTexts = filters.map((f) => f.label);
    const sliced = filterTexts.slice(0, maxDisplayCount);
    const remainingCount = filterTexts.length - maxDisplayCount;

    let filterText = sliced.length ? `選択中：${sliced.join('、')}` : '';
    filterText = filterText.replace(/【[^】]*】/g, '');
    if (remainingCount > 0) {
      filterText += ` +あと${remainingCount}個`;
    }

    return filterText;
  };

  const selectedServiceAndAdditionStatusCount = (): number => {
    return getSelectedSearchFiltersByGroup('serviceAndAdditionStatus').length;
  };
  const selectedServiceAndAdditionStatusText = (): string => {
    const filters = getSelectedSearchFiltersByGroup('serviceAndAdditionStatus');
    return getSelectedSearchFilterTexts(filters);
  };

  // サーバー側検索により、URLパラメータ変更時はNext.jsが自動的に再検索を実行

  const handleSearch = async (formValue: SearchStationDetailType) => {
    // ページ番号を1にリセット
    formMethod.setValue('page', 1);

    // サーバー側検索のためルーター遷移のみ実行
    searchRoutePush({
      searchType: formValue.searchType,
      postCode: formValue.location.zipcode,
      prefName: formValue.location.pref_name,
      address: formValue.location.address,
      partnerName: formValue.partnerName,
      movingMinutes: formValue.location.moving_minutes,
      sort: formValue.sort,
      filters: formValue.filters,
      isNoAddition: formValue.is_no_addition,
    });
  };

  const handleOpenSearchFilter = (
    event: React.MouseEvent<HTMLButtonElement>,
    searchFilterType: PublicSearchFilterType,
  ) => {
    setSearchFilterEl(event.currentTarget);
    setSearchFilterType(searchFilterType);
  };

  const handleCloseSearchFilter = () => {
    setSearchFilterEl(null);
    setSearchFilterType(null);
  };

  const handleToggleMap = () => {
    const newQuery = new URLSearchParams(window.location.search);

    if (isShowMap) {
      newQuery.delete(
        pathInfo['/public/stations/search'].searchParams.isShowMap,
      );
    } else {
      newQuery.set(
        pathInfo['/public/stations/search'].searchParams.isShowMap,
        'true',
      );
    }

    router.push(`/public/stations/search?${newQuery.toString()}`);

    setIsShowMap(!isShowMap);
  };

  const onChangePagination: PaginationProps['onChange'] = async (_, page) => {
    await gotoPageForPagination(page);
  };

  // スクロールなしのホバー処理（PublicListingListItem用）
  const handleSetHoveredMarkerWithoutScroll = React.useCallback(
    (listingId: number | null) => {
      if (listingId !== null) {
        // ホバー開始の場合は即座に設定（スクロールなし）
        setHoveredMarkerListingId(listingId);
      }
      // ホバー解除の場合は何もしない（ONのまま維持）
    },
    [],
  );

  // マーカークリック時の処理（InfoWindow制御 + スクロール）
  const handleMarkerClick = React.useCallback(
    (listingId: number) => {
      // 常にスクロールを実行
      if (listingListRef.current) {
        listingListRef.current.scrollToListing(listingId, 'click');
      }

      // InfoWindow の ON/OFF トグル
      if (hoveredMarkerListingId === listingId) {
        setHoveredMarkerListingId(null);
      } else {
        setHoveredMarkerListingId(listingId);
      }
    },
    [hoveredMarkerListingId, listingListRef],
  );

  // ページング後のlistings変更を検知してスクロールと選択処理を実行
  React.useEffect(() => {
    const currentPage = defaultFormValuePage;
    const prevPage = prevPageRef.current;

    // ページが変更された場合のみ処理を実行
    if (currentPage !== prevPage && listings && listings.length > 0) {
      // ListingListのスクロールを最上部に移動
      if (listingListRef.current) {
        listingListRef.current.scrollToListing(listings[0]?.id || 0, 'click');
      }

      // 最初のステーションを選択（マップ表示時のみ）
      if (isShowMap) {
        const firstListing = listings[0];
        const firstId = firstListing?.id;
        if (firstId) {
          setHoveredMarkerListingId(firstId);
        }
      }
    }

    // 現在のページを記録
    prevPageRef.current = currentPage;
  }, [listings, defaultFormValuePage, isShowMap]);

  return {
    isMobile,
    formMethod,
    paginationInfo,
    searchFilterEl,
    isShowMap,
    searchFilterType,
    targetAddress,
    listings,
    handleSearch,
    handleOpenSearchFilter,
    handleCloseSearchFilter,
    handleToggleMap,
    gotoPageForPagination,
    onChangePagination,
    hoveredMarkerListingId,
    setHoveredMarkerListingIdWithoutScroll: handleSetHoveredMarkerWithoutScroll,
    handleMarkerClick,
    listingListRef,
    selectedServiceAndAdditionStatusCount,
    selectedServiceAndAdditionStatusText,
    selectedSearchFilters: defaultFormValueSearchFilters,
  };
};
